import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PDFViewer } from "@react-pdf/renderer";

import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Cancel, Paid, Print, ReceiptLong } from "@mui/icons-material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import Toast from "../../components/Layout/Toast";
import { selectAuth } from "../auth/authSlice";
import { RoleTypes } from "../auth/types/roleTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import { selectPreference } from "../preferences/preferencesSlice";
import {
  cancelInvoice,
  getInvoice,
  recordInvoicePayment,
  selectInvoices,
} from "./invoiceSlices";
import { InvoiceStatus } from "./types/invoiceTypes";
import { InvoicePdf } from "./components/InvoicePdf";
import { SectionCard } from "./components/SectionCard";

const STATUS_COLOR: Record<
  InvoiceStatus,
  "default" | "warning" | "success" | "error"
> = {
  [InvoiceStatus.unpaid]: "error",
  [InvoiceStatus.partial]: "warning",
  [InvoiceStatus.paid]: "success",
  [InvoiceStatus.cancelled]: "default",
};

export const InvoiceDetail = () => {
  const { id } = useParams() as { id: string };
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const { locale } = useAppSelector(selectPreference);
  const {
    selectedInvoice: invoice,
    loading,
    success,
    error,
  } = useAppSelector(selectInvoices);

  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState<Date | null>(new Date());
  const [paymentRemark, setPaymentRemark] = useState("");
  const [showPdf, setShowPdf] = useState(false);

  const canManage = isPrivilegedTransaction(
    user?.roles ?? [],
    RoleTypes.ManageInvoicing,
  );

  useEffect(() => {
    if (id) dispatch(getInvoice(id));
  }, [dispatch, id]);

  const handleRecordPayment = async () => {
    await dispatch(
      recordInvoicePayment({
        invoiceId: id,
        amount: Number(paymentAmount),
        paymentDate: paymentDate ?? undefined,
        remark: paymentRemark || undefined,
      }),
    );
    setPaymentAmount("");
    setPaymentRemark("");
  };

  const handleCancel = () => {
    if (
      window.confirm(
        "Cancel this invoice? Its employees become available for a new invoice.",
      )
    ) {
      dispatch(cancelInvoice(id));
    }
  };

  const isCancelled = invoice?.status === InvoiceStatus.cancelled;

  return (
    <>
      <Helmet>
        <title>{`${invoice?.invoiceNumber ?? "Invoice"} | ${t(
          "platform",
        )}`}</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        {success && <Toast severity="success">{success.message}</Toast>}
        {error && <Toast severity="error">{error.message}</Toast>}

        {invoice && (
          <>
            <SectionCard
              icon={<ReceiptLong />}
              title={invoice.invoiceNumber}
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={te(invoice.status as string)}
                    color={STATUS_COLOR[invoice.status as InvoiceStatus]}
                    sx={{ fontWeight: "bold" }}
                  />
                  <Button
                    color="inherit"
                    variant="outlined"
                    onClick={() => setShowPdf(!showPdf)}
                  >
                    <Print /> {showPdf ? "Hide" : "Print"} PDF
                  </Button>
                  {canManage && !isCancelled && (
                    <Button
                      color="error"
                      variant="contained"
                      onClick={handleCancel}
                    >
                      <Cancel /> Cancel Invoice
                    </Button>
                  )}
                </Stack>
              }
            >
              <Grid container spacing={2}>
                <Grid item md={4} xs={12}>
                  <Typography color="textSecondary" variant="body2">
                    Agent
                  </Typography>
                  <Typography variant="h6">
                    {invoice.agent?.displayName}
                  </Typography>
                </Grid>
                <Grid item md={4} xs={12}>
                  <Typography color="textSecondary" variant="body2">
                    Issue Date
                  </Typography>
                  <Typography variant="h6">
                    {invoice.issueDate &&
                      format(new Date(invoice.issueDate), "dd-MMM-yyyy")}
                  </Typography>
                </Grid>
                {invoice.remark && (
                  <Grid item md={4} xs={12}>
                    <Typography color="textSecondary" variant="body2">
                      Remark
                    </Typography>
                    <Typography>{invoice.remark}</Typography>
                  </Grid>
                )}
                {[
                  { label: "Total", value: invoice.totalOwed },
                  { label: "Paid", value: invoice.amountPaid },
                  { label: "Balance", value: invoice.balance },
                ].map((stat) => (
                  <Grid item md={4} xs={12} key={stat.label}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        textAlign: "center",
                        borderRadius: "8px",
                        borderColor:
                          stat.label === "Balance" && (stat.value ?? 0) > 0
                            ? "error.main"
                            : "divider",
                      }}
                    >
                      <Typography color="textSecondary" variant="body2">
                        {stat.label} (USD)
                      </Typography>
                      <Typography variant="h5" component="div">
                        {(stat.value ?? 0).toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </SectionCard>

            <TableContainer
              component={Paper}
              elevation={3}
              sx={{ borderRadius: "8px" }}
            >
              <Typography variant="h6" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                Employees ({invoice.lineItems?.length ?? 0})
              </Typography>
              <Table size="small" aria-label="invoice line items">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>S.No</StyledTableCell>
                    <StyledTableCell>Full Name</StyledTableCell>
                    <StyledTableCell>Passport No.</StyledTableCell>
                    <StyledTableCell>{t("country")}</StyledTableCell>
                    <StyledTableCell>Departed On</StyledTableCell>
                    <StyledTableCell align="right">
                      Owed Amount (USD)
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {(invoice.lineItems ?? []).map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      <StyledTableCell>
                        {item.employee?.givenName} {item.employee?.surName}
                      </StyledTableCell>
                      <StyledTableCell>
                        {item.employee?.passportNumber}
                      </StyledTableCell>
                      <StyledTableCell>
                        {te(
                          (item.employee?.additionalData
                            ?.appliedCountry as string) ?? "",
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        {item.employee?.statusDates?.departed &&
                          format(
                            new Date(item.employee.statusDates.departed),
                            "dd-MMM-yyyy",
                          )}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {item.owedAmount?.toLocaleString()}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer
              component={Paper}
              elevation={3}
              sx={{ mt: 2, borderRadius: "8px" }}
            >
              <Typography variant="h6" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                Payments ({invoice.payments?.length ?? 0})
              </Typography>
              <Table size="small" aria-label="invoice payments">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>S.No</StyledTableCell>
                    <StyledTableCell>Payment Date</StyledTableCell>
                    <StyledTableCell align="right">
                      Amount (USD)
                    </StyledTableCell>
                    <StyledTableCell>Remark</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {(invoice.payments ?? []).length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={4}>
                        No payments recorded yet
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    (invoice.payments ?? []).map((payment, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell>{index + 1}</StyledTableCell>
                        <StyledTableCell>
                          {payment.paymentDate &&
                            format(
                              new Date(payment.paymentDate),
                              "dd-MMM-yyyy",
                            )}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {payment.amount?.toLocaleString()}
                        </StyledTableCell>
                        <StyledTableCell>{payment.remark}</StyledTableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {canManage && !isCancelled && (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={locale}
              >
                <SectionCard icon={<Paid />} title="Record Payment" sx={{ mt: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                  <Grid item md={3} xs={12}>
                    <TextField
                      id="paymentAmount"
                      label="Payment Amount (USD)"
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <DatePicker
                      label="Payment Date"
                      disableMaskedInput
                      views={["year", "month", "day"]}
                      inputFormat="DD-MMM-YYYY"
                      value={paymentDate ? dayjs(paymentDate) : null}
                      onChange={(newValue: Dayjs | null) =>
                        setPaymentDate(newValue ? newValue.toDate() : null)
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <TextField
                      id="paymentRemark"
                      label="Remark"
                      value={paymentRemark}
                      onChange={(e) => setPaymentRemark(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <Button
                      color="secondary"
                      variant="contained"
                      fullWidth
                      disabled={
                        loading === "pending" || !(Number(paymentAmount) > 0)
                      }
                      onClick={handleRecordPayment}
                    >
                      <Paid /> Record Payment
                    </Button>
                  </Grid>
                  </Grid>
                </SectionCard>
              </LocalizationProvider>
            )}

            {showPdf && user && (
              <Paper
                elevation={3}
                sx={{ mt: 2, borderRadius: "8px", overflow: "hidden" }}
              >
                <PDFViewer style={{ height: "900px", width: "100%" }}>
                  <InvoicePdf invoice={invoice} user={user} />
                </PDFViewer>
              </Paper>
            )}
          </>
        )}
      </Box>
    </>
  );
};
